import { useState, useEffect, useRef, useCallback } from 'react'

// ============ DATA ============
interface Artist {
  id: string
  name: string
  username: string
  avatar: string
  bio: string
  verified: boolean
}

interface Artwork {
  id: string
  title: string
  description: string
  image: string
  artist: Artist
  medium: string
  year: string
  dimensions: string
  edition: string
  aiPassed: boolean
}

const artists: Artist[] = [
  { id: 'a1', name: 'Marc Dubois', username: '@marcdubois', avatar: 'M', bio: 'Contemporary painter exploring light and shadow', verified: true },
  { id: 'a2', name: 'Sofia Renard', username: '@sofiarenard', bio: 'Sculptor working in marble and bronze', verified: true, avatar: 'S' },
  { id: 'a3', name: 'Pierre Lalonde', username: '@pierrel', bio: 'Digital artist bridging classical and modern', verified: false, avatar: 'P' },
  { id: 'a4', name: 'Isabella Voss', username: '@isavoss', bio: 'Mixed media collage and oil', verified: true, avatar: 'I' },
]

const artworks: Artwork[] = [
  { id: 'w1', title: 'The Celestial Ceiling', description: 'A masterpiece of chiaroscuro technique, capturing the interplay of divine light and mortal shadow. Each brushstroke reveals centuries of artisan mastery.', image: '/assets/5jpg.jpg', artist: artists[0], medium: 'Oil on Canvas', year: '2024', dimensions: '120 × 80 cm', edition: 'Original · 1 of 1', aiPassed: true },
  { id: 'w2', title: 'Pyramid at Dusk', description: 'The glass pyramid bathed in the last golden hour light of Paris. A meditation on modernity meeting history.', image: '/assets/Untitled2.jpg', artist: artists[1], medium: 'Photography', year: '2025', dimensions: '60 × 90 cm', edition: 'Digital · Edition 5 of 10', aiPassed: true },
  { id: 'w3', title: 'Marble & Silence', description: 'Carved from a single block of Carrara marble. The surface holds a silence no algorithm could replicate.', image: '/assets/7.jpg', artist: artists[0], medium: 'Mixed Media', year: '2024', dimensions: '100 × 100 cm', edition: 'Original · 1 of 1', aiPassed: true },
  { id: 'w4', title: 'Inverted Reflection', description: 'Looking up through the inverted pyramid — the world turned inside out through glass and steel.', image: '/assets/9.jpg', artist: artists[2], medium: 'Digital Print', year: '2025', dimensions: '80 × 60 cm', edition: 'Digital · Edition 3 of 10', aiPassed: true },
  { id: 'w5', title: 'Golden Hour Rotunda', description: 'The grand rotunda flooded with afternoon sun. Frescoes glow. Visitors wander. Time slows.', image: '/assets/12.jpg', artist: artists[3], medium: 'Oil on Linen', year: '2024', dimensions: '150 × 100 cm', edition: 'Original · 1 of 1', aiPassed: true },
  { id: 'w6', title: 'Night in Red', description: 'The pyramid ablaze in crimson light, reflected perfectly in the still pool. A moment of impossible beauty.', image: '/assets/11.jpg', artist: artists[1], medium: 'Photography', year: '2025', dimensions: '90 × 60 cm', edition: 'Original Print', aiPassed: true },
]

// ============ TYPES ============
type ViewName = 'landing' | 'auth' | 'feed' | 'artist-gallery' | 'artwork-detail' | 'enlarged' | 'mint' | 'checkout' | 'dms' | 'profile'
type CartItem = { artwork: Artwork; quantity: number }

interface AppState {
  currentView: ViewName
  feedIndex: number
  artistIndex: number
  artistFeed: Artwork[]
  enlargedArtwork: Artwork | null
  selectedArtwork: Artwork | null
  cart: CartItem[]
  lastFeedView: { type: 'feed' | 'artist'; index: number } | null
  uiReady: boolean
  transitionOverlay: boolean
}

// ============ PARTICLES CANVAS ============
function ParticlesCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const particlesRef = useRef(
    Array.from({ length: 40 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      size: Math.random() * 1.5 + 0.3,
      speedX: (Math.random() - 0.5) * 0.3,
      speedY: -Math.random() * 0.5 - 0.1,
      opacity: Math.random() * 0.5 + 0.1,
      life: Math.random(),
    }))
  )

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight
    const handleResize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight }
    window.addEventListener('resize', handleResize)
    let animId: number
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      particlesRef.current.forEach(p => {
        p.x += p.speedX; p.y += p.speedY; p.life += 0.003
        if (p.y < -5 || p.life > 1) {
          p.x = Math.random() * canvas.width; p.y = canvas.height + 5; p.life = 0; p.opacity = Math.random() * 0.5 + 0.1
        }
        const fade = Math.sin(p.life * Math.PI)
        ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(201,168,76,${p.opacity * fade})`; ctx.fill()
      })
      animId = requestAnimationFrame(animate)
    }
    animate()
    return () => { window.removeEventListener('resize', handleResize); cancelAnimationFrame(animId) }
  }, [])

  return <canvas ref={canvasRef} id="particles" />
}

// ============ CUSTOM CURSOR ============
function CustomCursor() {
  const cursorRef = useRef<HTMLDivElement>(null)
  const ringRef = useRef<HTMLDivElement>(null)
  const pos = useRef({ mx: 0, my: 0, rx: 0, ry: 0 })

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      pos.current.mx = e.clientX; pos.current.my = e.clientY
      if (cursorRef.current) { cursorRef.current.style.left = e.clientX + 'px'; cursorRef.current.style.top = e.clientY + 'px' }
    }
    document.addEventListener('mousemove', handleMove)
    let animId: number
    const loop = () => {
      const p = pos.current
      p.rx += (p.mx - p.rx) * 0.12; p.ry += (p.my - p.ry) * 0.12
      if (ringRef.current) { ringRef.current.style.left = p.rx + 'px'; ringRef.current.style.top = p.ry + 'px' }
      animId = requestAnimationFrame(loop)
    }
    loop()
    const els = 'button, .artwork-post, .artist-card, .cart-icon, .acquire-icon'
    document.querySelectorAll(els).forEach(el => {
      el.addEventListener('mouseenter', () => { if (cursorRef.current) { cursorRef.current.style.width = '18px'; cursorRef.current.style.height = '18px' }; if (ringRef.current) { ringRef.current.style.width = '50px'; ringRef.current.style.height = '50px' } })
      el.addEventListener('mouseleave', () => { if (cursorRef.current) { cursorRef.current.style.width = '10px'; cursorRef.current.style.height = '10px' }; if (ringRef.current) { ringRef.current.style.width = '36px'; ringRef.current.style.height = '36px' } })
    })
    return () => { document.removeEventListener('mousemove', handleMove); cancelAnimationFrame(animId) }
  }, [])

  return (<><div ref={cursorRef} id="cursor" /><div ref={ringRef} id="cursor-ring" /></>)
}

// ============ HEADER BAR ============
function HeaderBar({ view, onNavigate, cartCount }: { view: ViewName; onNavigate: (v: ViewName) => void; cartCount: number }) {
  return (
    <header className="app-header">
      <button className="header-logo" onClick={() => onNavigate('feed')}>AllThings<span>Art</span></button>
      <div className="header-nav">
        <button className={`header-nav-btn ${view === 'feed' || view === 'artist-gallery' ? 'active' : ''}`} onClick={() => onNavigate('feed')}>Gallery</button>
        <button className={`header-nav-btn ${view === 'dms' ? 'active' : ''}`} onClick={() => onNavigate('dms')}>Messages</button>
        <button className={`header-nav-btn ${view === 'profile' ? 'active' : ''}`} onClick={() => onNavigate('profile')}>Profile</button>
      </div>
      <div className="header-actions">
        <button className="header-icon-btn cart-icon" onClick={() => onNavigate('checkout')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
          {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
        </button>
        <button className="header-icon-btn acquire-icon" onClick={() => onNavigate('checkout')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
        </button>
      </div>
    </header>
  )
}

// ============ SCENE: LANDING ============
function LandingScene({ onEnter }: { onEnter: () => void }) {
  return (
    <div className="scene scene-landing">
      <div className="landing-content">
        <h1 className="landing-title">AllThingsArt</h1>
        <p className="landing-subtitle">Where Masterpieces Find Their Keepers</p>
        <p className="landing-desc">100% human-made art. Verified. Curated. Immortalized.</p>
        <button className="landing-cta" onClick={onEnter}>Enter the Gallery</button>
      </div>
    </div>
  )
}

// ============ SCENE: AUTH ============
function AuthScene({ onEnter }: { onEnter: () => void }) {
  const [tab, setTab] = useState<'login' | 'signup'>('login')
  return (
    <div className="scene scene-auth">
      <div className="auth-glass glass">
        <h2 className="auth-title">Entrance</h2>
        <div className="auth-tabs">
          <button className={`auth-tab ${tab === 'login' ? 'active' : ''}`} onClick={() => setTab('login')}>Login</button>
          <button className={`auth-tab ${tab === 'signup' ? 'active' : ''}`} onClick={() => setTab('signup')}>Register</button>
        </div>
        <form className="auth-form" style={{ display: tab === 'login' ? '' : 'none' }} onSubmit={e => { e.preventDefault(); onEnter() }}>
          <input className="auth-input" type="email" placeholder="Email" />
          <input className="auth-input" type="password" placeholder="Password" />
          <button className="auth-btn" type="submit">Enter</button>
          <div className="auth-divider" />
          <button className="wallet-btn" type="button">Connect Wallet</button>
        </form>
        <form className="auth-form" style={{ display: tab === 'signup' ? '' : 'none' }} onSubmit={e => { e.preventDefault(); onEnter() }}>
          <input className="auth-input" type="text" placeholder="Username" />
          <input className="auth-input" type="email" placeholder="Email" />
          <input className="auth-input" type="password" placeholder="Password" />
          <button className="auth-btn" type="submit">Register</button>
          <div className="auth-divider" />
          <button className="wallet-btn" type="button">Connect Wallet</button>
        </form>
      </div>
    </div>
  )
}

// ============ FEED POST COMPONENT ============
function FeedPost({ artwork, onView, onArtistClick }: { artwork: Artwork; onView: () => void; onArtistClick: () => void }) {
  return (
    <article className="artwork-post">
      <div className="post-header" onClick={onArtistClick}>
        <div className="post-avatar">{artwork.artist.avatar}</div>
        <div className="post-artist-info">
          <span className="post-artist-name">{artwork.artist.name}</span>
          {artwork.artist.verified && <span className="verified-badge">✓</span>}
          <span className="post-artist-handle">{artwork.artist.username}</span>
        </div>
      </div>
      <div className="post-image-container" onClick={onView}>
        <img className="post-image" src={artwork.image} alt={artwork.title} loading="lazy" />
      </div>
      <div className="post-footer">
        <div className="post-actions">
          <button className="post-action-btn">♡</button>
          <button className="post-action-btn">💬</button>
          <button className="post-action-btn">↗</button>
        </div>
        <div className="post-caption">
          <span className="post-caption-artist" onClick={(e) => { e.stopPropagation(); onArtistClick() }}>{artwork.artist.name}</span>
          {' '}<span className="post-caption-text"><strong>{artwork.title}</strong> — {artwork.description.substring(0, 80)}…</span>
        </div>
      </div>
    </article>
  )
}

// ============ SCENE: FEED ============
function FeedScene({ onArtworkClick, onArtistClick }: { onArtworkClick: (a: Artwork) => void; onArtistClick: (a: Artwork) => void }) {
  return (
    <div className="scene scene-feed">
      <div className="feed-container">
        {artworks.map(a => (
          <FeedPost key={a.id} artwork={a} onView={() => onArtworkClick(a)} onArtistClick={() => onArtistClick(a)} />
        ))}
      </div>
    </div>
  )
}

// ============ SCENE: ARTIST GALLERY ============
function ArtistGalleryScene({ artworks: artistWorks, onArtworkClick, onBack }: { artworks: Artwork[]; onArtworkClick: (a: Artwork) => void; onBack: () => void }) {
  const artist = artistWorks[0]?.artist
  if (!artist) return null
  const artistTotalWorks = artistWorks.length
  return (
    <div className="scene scene-artist-gallery">
      <div className="feed-container">
        <div className="artist-gallery-header glass">
          <button className="artist-back-btn" onClick={onBack}>← Back to Gallery</button>
          <div className="artist-profile-card">
            <div className="artist-profile-avatar">{artist.avatar}</div>
            <div className="artist-profile-info">
              <div className="artist-profile-name">{artist.name} {artist.verified && <span className="verified-badge">✓</span>}</div>
              <div className="artist-profile-handle">{artist.username}</div>
              <p className="artist-profile-bio">{artist.bio}</p>
              <div className="artist-profile-stats">
                <span>{artistTotalWorks} works</span>
                <span>·</span>
                <span>Verified Artist</span>
              </div>
            </div>
          </div>
        </div>
        {artistWorks.map(a => (
          <FeedPost key={a.id} artwork={a} onView={() => onArtworkClick(a)} onArtistClick={() => {}} />
        ))}
      </div>
    </div>
  )
}

// ============ SCENE: ENLARGED ARTWORK (Instagram-style) ============
function EnlargedView({ artwork, onNext, onPrev, onArtistClick, onMoreInfo }: {
  artwork: Artwork; onNext: () => void; onPrev: () => void; onArtistClick: () => void; onMoreInfo: () => void
}) {
  const touchStart = useRef(0)
  return (
    <div className="scene scene-enlarged"
      onTouchStart={e => { touchStart.current = e.touches[0].clientY }}
      onTouchEnd={e => {
        const diff = touchStart.current - e.changedTouches[0].clientY
        if (Math.abs(diff) > 60) { diff > 0 ? onNext() : onPrev() }
      }}>
      <div className="enlarged-container">
        <button className="enlarged-close" onClick={onPrev}>✕</button>
        <div className="enlarged-content" onClick={onArtistClick}>
          <img className="enlarged-image" src={artwork.image} alt={artwork.title} />
          <div className="enlarged-overlay">
            <div className="enlarged-info glass">
              <div className="enlarged-header" onClick={(e) => { e.stopPropagation(); onArtistClick() }}>
                <div className="enlarged-artist-avatar">{artwork.artist.avatar}</div>
                <div>
                  <div className="enlarged-artist-name">{artwork.artist.name} {artwork.artist.verified && <span className="verified-badge">✓</span>}</div>
                  <div className="enlarged-artist-handle">{artwork.artist.username}</div>
                </div>
              </div>
              <h2 className="enlarged-title">{artwork.title}</h2>
              <p className="enlarged-description">{artwork.description}</p>
              <div className="enlarged-details">
                <div className="detail-item"><span className="detail-label">Medium</span><span className="detail-value">{artwork.medium}</span></div>
                <div className="detail-item"><span className="detail-label">Year</span><span className="detail-value">{artwork.year}</span></div>
                <div className="detail-item"><span className="detail-label">Dimensions</span><span className="detail-value">{artwork.dimensions}</span></div>
                <div className="detail-item"><span className="detail-label">Edition</span><span className="detail-value">{artwork.edition}</span></div>
                <div className="detail-item"><span className="detail-label">AI Verified</span><span className="detail-value verified">✓ 100% Human-Made</span></div>
              </div>
              <button className="more-info-btn" onClick={(e) => { e.stopPropagation(); onMoreInfo() }}>More Info</button>
            </div>
          </div>
        </div>
        <button className="nav-arrow nav-prev" onClick={(e) => { e.stopPropagation(); onPrev() }}>‹</button>
        <button className="nav-arrow nav-next" onClick={(e) => { e.stopPropagation(); onNext() }}>›</button>
      </div>
    </div>
  )
}

// ============ SCENE: MINT ============
function MintScene({ artwork, onCheckout, onBack }: { artwork: Artwork; onCheckout: () => void; onBack: () => void }) {
  const [edition, setEdition] = useState('1 of 1')
  const [royalty, setRoyalty] = useState('10%')
  return (
    <div className="scene scene-mint">
      <div className="mint-layout">
        <div className="mint-stage">
          <div className="mint-hologram">
            <div className="hologram-ring" /><div className="hologram-ring-2" />
            <div className="hologram-core"><img className="hologram-img" src={artwork.image} alt="Artwork" /></div>
            <div className="hologram-scanlines" />
          </div>
          <div className="mint-progress">
            <div className="mint-step done"><div className="step-dot" />Upload</div>
            <div className="mint-step-sep" />
            <div className="mint-step current"><div className="step-dot" />Configure</div>
            <div className="mint-step-sep" />
            <div className="mint-step"><div className="step-dot" />Mint</div>
          </div>
          <div style={{ fontSize: 10, color: 'rgba(245,240,232,0.3)', letterSpacing: '0.25em', fontStyle: 'italic' }}>{artwork.title}</div>
        </div>
        <div className="mint-form glass">
          <div className="mint-title">Atelier — Acquire This Work</div>
          <p className="mint-subtitle">Digitize. Authenticate. Immortalize.</p>
          <div className="mint-field">
            <label className="mint-label">Title</label>
            <div className="mint-input-display">{artwork.title}</div>
          </div>
          <div className="mint-field">
            <label className="mint-label">Artist</label>
            <div className="mint-input-display">{artwork.artist.name}</div>
          </div>
          <div className="mint-field">
            <label className="mint-label">Edition Size</label>
            <div className="mint-royalty">
              {['1 of 1', 'Edition 5', 'Edition 10'].map(e => (
                <button key={e} className={`royalty-btn ${edition === e ? 'selected' : ''}`} onClick={() => setEdition(e)}>{e}</button>
              ))}
            </div>
          </div>
          <div className="mint-field">
            <label className="mint-label">Your Royalty</label>
            <div className="mint-royalty">
              {['5%', '10%', '15%'].map(r => (
                <button key={r} className={`royalty-btn ${royalty === r ? 'selected' : ''}`} onClick={() => setRoyalty(r)}>{r}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="mint-fee-row"><span className="mint-fee-label">Gas Fee (est.)</span><span className="mint-fee-val">≈ 0.004 ETH</span></div>
            <div className="mint-fee-row"><span className="mint-fee-label">Platform Fee</span><span className="mint-fee-val">2.5%</span></div>
            <div className="mint-fee-row"><span className="mint-fee-label">Your Royalty</span><span className="mint-fee-val">{royalty} perpetual</span></div>
          </div>
          <button className="btn-mint" onClick={onCheckout}>Proceed to Acquisition</button>
          <button className="btn-back-gallery" onClick={onBack}>Return to Gallery</button>
        </div>
      </div>
    </div>
  )
}

// ============ SCENE: CHECKOUT ============
function CheckoutScene({ cart, onPurchase, onReturn }: { cart: CartItem[]; onPurchase: () => void; onReturn: () => void }) {
  const subtotal = 58300
  return (
    <div className="scene scene-checkout">
      <div className="checkout-layout">
        <div className="checkout-items">
          <div className="checkout-header">
            <h2 className="checkout-title">Acquisition</h2>
            <div className="checkout-count">{cart.length} work{cart.length !== 1 ? 's' : ''} selected</div>
          </div>
          {cart.length === 0 ? (
            <div className="empty-cart">
              <p>Your curation is empty.</p>
              <button className="btn-back-gallery" onClick={onReturn}>Return to Gallery</button>
            </div>
          ) : (
            <>
              {cart.map((item, i) => (
                <div key={i} className="cart-item">
                  <img className="cart-thumb" src={item.artwork.image} alt={item.artwork.title} />
                  <div>
                    <div className="cart-item-name">{item.artwork.title}</div>
                    <div className="cart-item-artist">{item.artwork.artist.name}</div>
                    <div className="cart-item-type">{item.artwork.edition}</div>
                  </div>
                  <div>
                    <div className="cart-item-price">€ 31,000</div>
                    <button className="cart-item-remove">Remove</button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
        <div className="checkout-sidebar">
          <div className="order-summary glass">
            <div className="summary-title">Order Summary</div>
            <div className="summary-row"><span className="summary-label">Subtotal</span><span className="summary-val">€ {subtotal.toLocaleString()}</span></div>
            <div className="summary-row"><span className="summary-label">Platform fee (2.5%)</span><span className="summary-val">€ {Math.round(subtotal * 0.025).toLocaleString()}</span></div>
            <div className="summary-row"><span className="summary-label">Authentication</span><span className="summary-val">Included</span></div>
            <div className="summary-row"><span className="summary-label">White-glove delivery</span><span className="summary-val">€ 450</span></div>
            <div className="summary-total">
              <span className="total-label">Total</span>
              <span className="total-amount">€ {(subtotal + Math.round(subtotal * 0.025) + 450).toLocaleString()}</span>
            </div>
            <div className="promo-row">
              <input className="promo-input" placeholder="Promo code" />
              <button className="promo-btn">Apply</button>
            </div>
            <button className="btn-checkout" onClick={onPurchase}>Complete Acquisition</button>
            <button className="btn-back-gallery" onClick={onReturn}>Return to Gallery</button>
            <div className="security-note">
              <span className="security-icon">⬡</span>
              <span className="security-text">Encrypted · Escrow Protected</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============ SCENE: DMS ============
function DmsScene() {
  const [activeDm, setActiveDm] = useState(0)
  const dms = [
    { name: 'Marc Dubois', initial: 'M', preview: 'Interested in the Ceiling piece…', time: '2m' },
    { name: 'Sofia Renard', initial: 'S', preview: 'The Marble works are sublime', time: '1h' },
    { name: 'Pierre Lalonde', initial: 'P', preview: 'Can we negotiate provenance?', time: '3h' },
  ]
  const messages = [
    { from: 'them' as const, text: 'The Celestial Ceiling piece — I\'ve been watching it for weeks.', time: '14:22' },
    { from: 'me' as const, text: 'The documentation is complete and fully authenticated.', time: '14:24' },
    { from: 'them' as const, text: 'Extraordinary. Would you consider a private viewing?', time: '14:26' },
    { from: 'me' as const, text: 'Absolutely. Shall we say this Thursday?', time: '14:28' },
  ]
  return (
    <div className="scene scene-dms">
      <div className="dms-layout glass">
        <div className="dms-list">
          <div className="dms-list-header"><div className="dms-list-title">Private Rooms</div></div>
          {dms.map((dm, i) => (
            <div key={i} className={`dm-item ${activeDm === i ? 'active' : ''}`} onClick={() => setActiveDm(i)}>
              <div className="dm-avatar">{dm.initial}</div>
              <div className="dm-info">
                <div className="dm-name">{dm.name}</div>
                <div className="dm-preview">{dm.preview}</div>
              </div>
              <div className="dm-time">{dm.time}</div>
            </div>
          ))}
        </div>
        <div className="dms-chat">
          <div className="chat-header">
            <div className="chat-avatar">{dms[activeDm].initial}</div>
            <div><div className="chat-name">{dms[activeDm].name}</div><div className="chat-status">Online</div></div>
          </div>
          <div className="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`msg ${m.from === 'them' ? 'recv' : 'sent'}`}>
                <div className="msg-bubble">{m.text}</div>
                <div className="msg-time">{m.time}</div>
              </div>
            ))}
          </div>
          <div className="chat-input-area">
            <input className="chat-input" placeholder="Write a message…" />
            <button className="chat-send">Send</button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============ SCENE: PROFILE ============
function ProfileScene() {
  const myWorks = artworks.slice(0, 3)
  return (
    <div className="scene scene-profile">
      <div className="profile-container">
        <div className="profile-header-card glass">
          <div className="profile-avatar-large">A</div>
          <div className="profile-info">
            <div className="profile-name">Alexandre Voss</div>
            <div className="profile-handle">@alexvoss</div>
            <p className="profile-bio">Curator, collector, and verifier of human-made art.</p>
            <div className="profile-stats">
              <div className="profile-stat"><div className="profile-stat-value">24</div><div className="profile-stat-label">Works</div></div>
              <div className="profile-stat"><div className="profile-stat-value">142</div><div className="profile-stat-label">Sales</div></div>
              <div className="profile-stat"><div className="profile-stat-value">8.2K</div><div className="profile-stat-label">Followers</div></div>
            </div>
          </div>
        </div>
        <div className="profile-section-title">Your Collection</div>
        <div className="collection-grid">
          {myWorks.map((a, i) => (
            <div key={i} className="collection-card">
              <img className="collection-thumb" src={a.image} alt={a.title} />
              <div className="collection-name">{a.title}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ============ MAIN APP ============
function App() {
  const [state, setState] = useState<AppState>({
    currentView: 'landing',
    feedIndex: 0,
    artistIndex: 0,
    artistFeed: [],
    enlargedArtwork: null,
    selectedArtwork: null,
    cart: [],
    lastFeedView: null,
    uiReady: false,
    transitionOverlay: false,
  })

  // Show UI after delay
  useEffect(() => { const t = setTimeout(() => setState(s => ({ ...s, uiReady: true })), 1500); return () => clearTimeout(t) }, [])

  // Parallax
  const parallaxRef = useRef({ currX: 0, currY: 0, targetX: 0, targetY: 0 })
  const layerBgRef = useRef<HTMLDivElement>(null)
  const layerMidRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      parallaxRef.current.targetX = (e.clientX / window.innerWidth - 0.5) * 15
      parallaxRef.current.targetY = (e.clientY / window.innerHeight - 0.5) * 10
    }
    document.addEventListener('mousemove', handleMove)
    let animId: number
    const loop = () => {
      const p = parallaxRef.current
      p.currX += (p.targetX - p.currX) * 0.04; p.currY += (p.targetY - p.currY) * 0.04
      if (layerBgRef.current) layerBgRef.current.style.transform = `translate(${p.currX * 0.4}px, ${p.currY * 0.4}px) scale(1.08)`
      if (layerMidRef.current) layerMidRef.current.style.transform = `translate(${p.currX * 0.7}px, ${p.currY * 0.7}px) scale(1.06)`
      animId = requestAnimationFrame(loop)
    }
    loop()
    return () => { document.removeEventListener('mousemove', handleMove); cancelAnimationFrame(animId) }
  }, [])

  const bgImg = state.enlargedArtwork ? state.enlargedArtwork.image : (state.artistFeed[0]?.image || '/assets/Untitled2.jpg')
  const midBgImg = state.enlargedArtwork ? '/assets/Untitled1.jpg' : '/assets/Untitled2.jpg'

  const navigate = useCallback((view: ViewName) => {
    setState(s => ({ ...s, transitionOverlay: true }))
    setTimeout(() => {
      setState(s => ({ ...s, currentView: view, transitionOverlay: false }))
    }, 500)
  }, [])

  const openArtwork = useCallback((artwork: Artwork) => {
    const idx = artworks.findIndex(a => a.id === artwork.id)
    setState(s => ({ ...s, enlargedArtwork: artwork, feedIndex: idx, currentView: 'enlarged', lastFeedView: s.lastFeedView || { type: 'feed', index: idx } }))
  }, [])

  const openArtistGallery = useCallback((artwork: Artwork) => {
    const artistWorks = artworks.filter(a => a.artist.id === artwork.artist.id)
    setState(s => ({ ...s, artistFeed: artistWorks, selectedArtwork: artwork, artistIndex: 0, currentView: 'artist-gallery' }))
  }, [])

  const openMoreInfo = useCallback((artwork: Artwork) => {
    setState(s => ({ ...s, selectedArtwork: artwork, currentView: 'mint' }))
  }, [])

  const goNextEnlarged = useCallback(() => {
    setState(s => {
      const src = s.artistFeed.length > 0 ? s.artistFeed : artworks
      const next = (s.feedIndex + 1) % src.length
      return { ...s, feedIndex: next, enlargedArtwork: src[next] }
    })
  }, [])

  const goPrevEnlarged = useCallback(() => {
    setState(s => {
      const src = s.artistFeed.length > 0 ? s.artistFeed : artworks
      const prev = (s.feedIndex - 1 + src.length) % src.length
      return { ...s, feedIndex: prev, enlargedArtwork: src[prev] }
    })
  }, [])

  const addToCart = useCallback((artwork: Artwork) => {
    setState(s => ({ ...s, cart: [...s.cart, { artwork, quantity: 1 }] }))
  }, [])

  const handlePurchase = useCallback(() => {
    alert('Acquisition complete! (Demo)')
    setState(s => ({ ...s, cart: [], currentView: 'feed' }))
  }, [])

  const returnToGallery = useCallback(() => {
    setState(s => {
      if (s.lastFeedView?.type === 'artist') {
        const artistWorks = artworks.filter(a => a.artist.id === s.artistFeed[0]?.artist.id)
        return { ...s, artistFeed: artistWorks, currentView: 'artist-gallery', enlargedArtwork: null }
      }
      return { ...s, currentView: 'feed', enlargedArtwork: null }
    })
  }, [])

  return (
    <div id="app">
      <CustomCursor />

      {/* Background */}
      <canvas id="bg-canvas" />
      <div className="parallax-world">
        <div ref={layerBgRef} className="layer layer-bg" style={{ backgroundImage: `url(${bgImg})` }} />
        <div ref={layerMidRef} className="layer layer-mid" style={{ backgroundImage: `url(${midBgImg})` }} />
        <div className="layer layer-vignette" />
      </div>
      <div id="light-rays" />
      <ParticlesCanvas />
      <div id="noise" />
      <div id="transition-overlay" style={{ opacity: state.transitionOverlay ? 1 : 0 }} />

      {/* Header */}
      {state.uiReady && state.currentView !== 'landing' && state.currentView !== 'auth' && (
        <HeaderBar view={state.currentView} onNavigate={navigate} cartCount={state.cart.length} />
      )}

      {/* Scenes */}
      {state.currentView === 'landing' && <LandingScene onEnter={() => navigate('auth')} />}
      {state.currentView === 'auth' && <AuthScene onEnter={() => navigate('feed')} />}
      {state.currentView === 'feed' && <FeedScene onArtworkClick={openArtwork} onArtistClick={openArtistGallery} />}
      {state.currentView === 'artist-gallery' && (
        <ArtistGalleryScene artworks={state.artistFeed} onArtworkClick={openArtwork} onBack={() => navigate('feed')} />
      )}
      {state.currentView === 'enlarged' && state.enlargedArtwork && (
        <EnlargedView artwork={state.enlargedArtwork} onNext={goNextEnlarged} onPrev={goPrevEnlarged}
          onArtistClick={() => {
            const artwork = state.enlargedArtwork!
            const artistWorks = artworks.filter(a => a.artist.id === artwork.artist.id)
            setState(s => ({ ...s, artistFeed: artistWorks, feedIndex: 0, currentView: 'artist-gallery' }))
          }}
          onMoreInfo={() => openMoreInfo(state.enlargedArtwork!)} />
      )}
      {state.currentView === 'mint' && state.selectedArtwork && (
        <MintScene artwork={state.selectedArtwork} onCheckout={() => { addToCart(state.selectedArtwork!); navigate('checkout') }} onBack={returnToGallery} />
      )}
      {state.currentView === 'checkout' && (
        <CheckoutScene cart={state.cart} onPurchase={handlePurchase} onReturn={returnToGallery} />
      )}
      {state.currentView === 'dms' && <DmsScene />}
      {state.currentView === 'profile' && <ProfileScene />}
    </div>
  )
}

export default App
